/**
 * Referral Partner Dashboard
 * Partners can view their balance, referral history, and request payouts
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  DollarSign, TrendingUp, ShoppingBag, Clock, Copy, LogOut,
  ArrowUpRight, Wallet, History, AlertCircle, CheckCircle, XCircle,
  Phone, Loader2
} from 'lucide-react';
import { api } from '@/utils/apiClient';

export default function ReferralDashboardPage() {
  const navigate = useNavigate();
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Dashboard data
  const [partner, setPartner] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [minPayoutAmount, setMinPayoutAmount] = useState(25);
  const [loading, setLoading] = useState(false);
  
  // Payout modal
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);
  
  // Check for saved session
  useEffect(() => {
    const savedPhone = localStorage.getItem('referral_partner_phone');
    if (savedPhone) {
      setPhoneNumber(savedPhone);
      handleLogin(null, savedPhone);
    }
  }, []);
  
  const handleLogin = async (e, savedPhone = null) => {
    if (e) e.preventDefault();
    const phone = savedPhone || phoneNumber;
    
    if (!phone) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setIsLoggingIn(true);
    
    try {
      const response = await api.get(`/referral-partners/dashboard/${phone}`);
      const data = response.data;
      
      setPartner(data.partner);
      setRecentTransactions(data.recent_transactions || []);
      setPendingPayouts(data.pending_payouts || []);
      setPayoutHistory(data.payout_history || []);
      setMinPayoutAmount(data.min_payout_amount || 25);
      
      localStorage.setItem('referral_partner_phone', phone);
      setIsLoggedIn(true);
      toast.success(`Welcome back, ${data.partner.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your phone number.');
      localStorage.removeItem('referral_partner_phone');
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('referral_partner_phone');
    setIsLoggedIn(false);
    setPartner(null);
    setPhoneNumber('');
  };
  
  const copyCode = () => {
    navigator.clipboard.writeText(partner?.code);
    toast.success('Code copied to clipboard!');
  };
  
  const refreshData = async () => {
    if (!partner) return;
    setLoading(true);
    try {
      const response = await api.get(`/referral-partners/dashboard/${partner.phone}`);
      const data = response.data;
      setPartner(data.partner);
      setRecentTransactions(data.recent_transactions || []);
      setPendingPayouts(data.pending_payouts || []);
      setPayoutHistory(data.payout_history || []);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    
    if (isNaN(amount) || amount < minPayoutAmount) {
      toast.error(`Minimum payout amount is $${minPayoutAmount}`);
      return;
    }
    
    if (amount > partner.commission_balance) {
      toast.error(`Insufficient balance. Your balance is $${partner.commission_balance.toFixed(2)}`);
      return;
    }
    
    setIsRequestingPayout(true);
    
    try {
      await api.post('/referral-partners/request-payout', {
        partner_id: partner.contact_id,
        amount: amount
      });
      
      toast.success('Payout request submitted! You will be notified when it is approved.');
      setShowPayoutModal(false);
      setPayoutAmount('');
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setIsRequestingPayout(false);
    }
  };
  
  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <img src="/logo.png" alt="Bulaccino" className="h-16 w-auto mx-auto" />
            </div>
            <CardTitle>Partner Dashboard</CardTitle>
            <CardDescription>Enter your phone number to access your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your registered phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  data-testid="referral-login-phone"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={isLoggingIn}
                data-testid="referral-login-submit"
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login to Dashboard'
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">Not a partner yet?</p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/referral-signup')}
              >
                Sign Up Now
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-amber-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Bulaccino" className="h-10 w-auto" />
            <div>
              <h1 className="font-bold text-lg text-gray-900 dark:text-white">{partner?.name}</h1>
              <p className="text-xs text-amber-600 dark:text-amber-400">Partner Dashboard</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Referral Code Banner */}
        <Card className="mb-8 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-amber-100 text-sm mb-1">Your Referral Code</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold tracking-wider">{partner?.code}</span>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={copyCode}
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    data-testid="copy-code-btn"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-amber-100 text-sm">Share with tourists → They save 5% → You earn 10%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Balance</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ${partner?.commission_balance?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Earned</p>
                  <p className="text-2xl font-bold">
                    ${partner?.total_commission_earned?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                  <p className="text-2xl font-bold">{partner?.total_referral_sales || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Paid Out</p>
                  <p className="text-2xl font-bold">
                    ${partner?.total_commission_paid_out?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button 
            onClick={() => setShowPayoutModal(true)}
            className="bg-green-600 hover:bg-green-700"
            disabled={partner?.commission_balance < minPayoutAmount || pendingPayouts.length > 0}
            data-testid="request-payout-btn"
          >
            <ArrowUpRight className="w-4 h-4 mr-2" />
            Request Cash Payout
          </Button>
          
          <Button variant="outline" onClick={refreshData} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <History className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
        
        {/* Pending Payouts Alert */}
        {pendingPayouts.length > 0 && (
          <Card className="mb-8 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">Payout Pending</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    You have requested ${pendingPayouts[0].amount_requested?.toFixed(2)} on {new Date(pendingPayouts[0].requested_at).toLocaleDateString()}. 
                    We&apos;ll notify you when it&apos;s approved.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Recent Transactions */}
          <Card className="bg-white dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="text-lg">Recent Referral Sales</CardTitle>
              <CardDescription>Commission earned from customer purchases</CardDescription>
            </CardHeader>
            <CardContent>
              {recentTransactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No referral sales yet</p>
                  <p className="text-sm">Share your code with tourists to start earning!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentTransactions.slice(0, 10).map((tx, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <p className="font-medium">Order #{tx.order_id?.slice(-6)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(tx.created_at).toLocaleDateString()} • Sale: ${tx.final_amount?.toFixed(2)}
                        </p>
                      </div>
                      <span className="text-green-600 font-semibold">
                        +${tx.commission_amount?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Payout History */}
          <Card className="bg-white dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="text-lg">Payout History</CardTitle>
              <CardDescription>Cash withdrawals from your account</CardDescription>
            </CardHeader>
            <CardContent>
              {payoutHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No payouts yet</p>
                  <p className="text-sm">Request a payout when your balance reaches ${minPayoutAmount}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payoutHistory.map((payout, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div className="flex items-center gap-2">
                        {payout.status === 'paid' ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : payout.status === 'rejected' ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500" />
                        )}
                        <div>
                          <p className="font-medium capitalize">{payout.status}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(payout.paid_at || payout.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className={payout.status === 'paid' ? 'text-green-600' : 'text-muted-foreground'}>
                        ${payout.amount_approved?.toFixed(2) || payout.amount_requested?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      {/* Payout Request Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Cash Payout</DialogTitle>
            <DialogDescription>
              Enter the amount you want to withdraw. Minimum payout is ${minPayoutAmount}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className="text-2xl font-bold text-green-600">
                ${partner?.commission_balance?.toFixed(2)}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="payout-amount">Payout Amount ($)</Label>
              <Input
                id="payout-amount"
                type="number"
                min={minPayoutAmount}
                max={partner?.commission_balance}
                step="0.01"
                placeholder={`Min $${minPayoutAmount}`}
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                data-testid="payout-amount-input"
              />
            </div>
            
            {partner?.commission_balance < minPayoutAmount && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>You need at least ${minPayoutAmount} to request a payout</span>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestPayout}
              disabled={isRequestingPayout || partner?.commission_balance < minPayoutAmount}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-payout-btn"
            >
              {isRequestingPayout ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Request Payout'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <footer className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>© {new Date().getFullYear()} Bulaccino Cafe. All rights reserved.</p>
      </footer>
    </div>
  );
}
